import { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Trans } from 'react-i18next';
import format from 'date-fns/format';
import logger from '@widgets/logger';
import { styled } from '@mui/material/styles';
import ExportButton from '@features/ExportButton';
import Paywall from '@features/Paywall';
import { ControlledToaster } from '@components/Toaster';
import { useTranslation, useSurveysOptions, useQueryParams, useLocations } from '@hooks';
import { PLUSPOINT_INTEGRATION, AccIntegrationName, ProfileName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { INCLUDE_COVER_MANAGER, INCLUDE_RECOVERY_FORM } from '@constants/feedbacks';
import { splitSurveyGroups } from '@helpers/surveyFilters';
import { channelsActions, feedbacksActions, labelsActions } from '@actions';
import { feedbacksService } from '@services';
import FiltersPanel from './FiltersPanel';
import Feedbacks from './Feedbacks';

const Container = styled('div')({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
});

const MINUTE = 1000 * 60;

const FeedbacksPage = () => {
    const params = useQueryParams();
    const queryParamsFeedbackId = params.get('feedbackId');

    const [filter, setFilter] = useState({
        companies: [],
        surveys: [],
        timeframe: { start: null, end: null },
        rating: params.get('rating') ? JSON.parse(params.get('rating')) : [1, 2, 3, 4, 5],
        statuses: params.get('statuses') ? JSON.parse(params.get('statuses')) : [0, 1, 2, 3],
        hideAnonimous: params.get('hideAnonimous') ? JSON.parse(params.get('hideAnonimous')) : true,
        feedbackId: queryParamsFeedbackId ? queryParamsFeedbackId : undefined,
    });
    const [openAlert, setOpenAlert] = useState(false);
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);
    const [applyChipsSignal, setApplyChipsSignal] = useState(0);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const account = useSelector(state => state.account?.account);
    const { feedbacks, pageToken, loading, newFeedbacksExist } = useSelector(state => state.feedbacks);
    const language = useSelector(state => state.auth.user?.language);

    const { t } = useTranslation();
    const isQrOnlyPlan = account?.surveysEnabled === false;

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { getSurveysByLocationIds } = useSurveysOptions();
    const { mapLocationsToFormValue } = useLocations();
    const firstRender = useRef(true);

    const isCoverManagerConnected = isIntegrationConnected(account, AccIntegrationName.CoverManager);

    useEffect(() => {
        dispatch(labelsActions.get());
        dispatch(channelsActions.get());
    }, []);

    useEffect(() => {
        if (locations.length) {
            const availableSurveys = getSurveysByLocationIds(locations.map(l => l.companyId));
            const surveys = [
                ...availableSurveys,
                ...(isCoverManagerConnected ? [INCLUDE_COVER_MANAGER] : []),
                INCLUDE_RECOVERY_FORM,
            ];
            const queryParamsFilter = [params.get('rating'), params.get('hideAnonimous'), params.get('statuses')];
            setFilterNumber(queryParamsFilter.filter(Boolean).length);
            setFilter(prev => ({ ...prev, surveys, companies: mapLocationsToFormValue() }));
            if (params.get('feedbackId')) {
                navigate({ search: '' }, { replace: true });
            }
        } else {
            dispatch(feedbacksActions.resetLoading());
        }
    }, [dispatch]);

    const buildQuery = f => ({
        ...f,
        ...splitSurveyGroups(f.surveys, { isCoverManagerConnected }),
        companies: f.companies.map(company => company.id),
        integrationNames: [PLUSPOINT_INTEGRATION],
        from: f.timeframe.start ? format(new Date(f.timeframe.start), 'dd/MMM/yyyy') : null,
        to: f.timeframe.end ? format(new Date(f.timeframe.end), 'dd/MMM/yyyy') : null,
        includeAnonymous: !f.hideAnonimous,
    });

    const currentQuery = useMemo(() => buildQuery(filter), [filter, isCoverManagerConnected]);

    useEffect(() => {
        if (queryParamsFeedbackId && !firstRender.current) {
            dispatch(feedbacksActions.getAll({ ...currentQuery, feedbackId: queryParamsFeedbackId }));
            navigate({ search: '' }, { replace: true });
        }
    }, [queryParamsFeedbackId]);

    useEffect(() => {
        if (currentQuery.companies.length && currentQuery.rating.length && currentQuery.statuses.length) {
            if (firstRender.current) {
                dispatch(feedbacksActions.getAll(currentQuery));
                firstRender.current = false;
            }
        }
    }, [currentQuery, dispatch]);

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const newExists = await feedbacksService.checkNewExist(currentQuery);
                dispatch(feedbacksActions.checkNewExistSuccess(newExists));
            } catch (_) {
                logger.error('Failed to check new feedbacks');
            }
        }, MINUTE);
        return () => clearInterval(interval);
    }, [currentQuery, dispatch]);

    useEffect(() => {
        if (newFeedbacksExist) {
            setOpenAlert(true);
        }
    }, [newFeedbacksExist]);

    const getNewReviews = () => {
        setOpenAlert(false);
        dispatch(feedbacksActions.resetNewExist());
        dispatch(feedbacksActions.getAll(currentQuery));
    };

    const onCloseToaster = () => dispatch(feedbacksActions.resetNewExist());

    const loadMore = () => {
        if (!pageToken) return;
        dispatch(feedbacksActions.loadMore(currentQuery, pageToken));
    };

    const onApplyFilters = () => {
        dispatch(feedbacksActions.getAll({ ...currentQuery, feedbackId: undefined }));
    };

    const getFeedType = () => {
        if (!filter.companies.length || !filter.rating.length || !filter.statuses.length) {
            return 1;
        }
        if (!feedbacks?.length && !firstRender.current) {
            return 3;
        }
        return 0;
    };

    const onUpdateContactCallback = () => dispatch(feedbacksActions.refetch(currentQuery, null, feedbacks.length));

    const onFilterBySurvey = (surveyTemplateId, group) => {
        const surveys = group ? [group] : surveyTemplateId ? [surveyTemplateId] : null;
        if (!surveys) return;
        const updatedFilter = { ...filter, surveys, feedbackId: undefined };
        setFilter(updatedFilter);
        dispatch(feedbacksActions.getAll(buildQuery(updatedFilter)));
        setApplyChipsSignal(prev => prev + 1);
    };

    const onFilterByLocation = locationId => {
        const location = locations.find(c => c.companyId === locationId);
        if (!location) return;
        const availableSurveys = getSurveysByLocationIds([locationId]);
        const locationHasCoverManager =
            isCoverManagerConnected && location.profiles?.some(p => p.type === ProfileName.CoverManager);
        const narrowedSurveys = filter.surveys.filter(
            s =>
                availableSurveys.includes(s) ||
                s === INCLUDE_RECOVERY_FORM ||
                (s === INCLUDE_COVER_MANAGER && locationHasCoverManager),
        );
        const fallbackSurveys = [
            ...availableSurveys,
            ...(locationHasCoverManager ? [INCLUDE_COVER_MANAGER] : []),
            INCLUDE_RECOVERY_FORM,
        ];
        const updatedFilter = {
            ...filter,
            companies: mapLocationsToFormValue([location]),
            surveys: narrowedSurveys.length ? narrowedSurveys : fallbackSurveys,
            feedbackId: undefined,
        };
        setFilter(updatedFilter);
        dispatch(feedbacksActions.getAll(buildQuery(updatedFilter)));
        setApplyChipsSignal(prev => prev + 1);
    };

    if (isQrOnlyPlan) {
        return (
            <Container>
                <Paywall />
            </Container>
        );
    }

    return (
        <Container>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    locations={locations}
                    filter={filter}
                    setFilter={setFilter}
                    type="feedbacks"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                    applyChipsSignal={applyChipsSignal}
                />
                <ExportButton callback={format => feedbacksService.exportCSV(currentQuery, format)} type="feedbacks" />
            </div>
            <Feedbacks
                data={feedbacks}
                language={language}
                loading={loading}
                pageToken={pageToken}
                fetchData={loadMore}
                feedType={getFeedType()}
                onUpdateContactCallback={onUpdateContactCallback}
                onInitConversationCallback={onUpdateContactCallback}
                onFilterByLocation={onFilterByLocation}
                onFilterBySurvey={onFilterBySurvey}
            />
            <ControlledToaster
                callback={getNewReviews}
                autoHideDuration={null}
                text={
                    <Trans t={t} i18nKey={'FeedbackResponses.newReviews'}>
                        <b style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={getNewReviews}>
                            {{ update: t('FeedbackResponses.update') }}
                        </b>
                    </Trans>
                }
                type="info"
                open={openAlert}
                setOpen={setOpenAlert}
                clickClose={onCloseToaster}
            />
        </Container>
    );
};

export default FeedbacksPage;
